import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { PhxBreakpointObserverService } from '../../services/phx-breakpoint-service';
import { Cross, ArrowLeft } from '../icon/icon-map';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';

@Component({
  selector: 'phx-drawer',
  templateUrl: './phx-drawer.component.html',
  styleUrls: ['./phx-drawer.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhxDrawerComponent implements OnInit, OnDestroy {
  /**
   * Indicates whether the drawer is open or closed.
   */
  @Input() phxOpen: boolean = false;

  /**
   * Indicates whether the drawer should automatically close when interacting with the backdrop, pressing ESC key or clicking the close button.
   * Defaults to true.
   */
  @Input() phxAutoClose: boolean = true;

  /**
   * Change the footer style of the drawer. Specifically to show info text in the footer.
   */
  @Input() phxInfoFooter: boolean = false;

  /**
   * Indicates whether the drawer has a navigation icon. Shows a back icon instead of close icon.
   */
  @Input() phxNavigationIcon: boolean = false;

  /**
   * Indicates whether the drawer close icon should be disabled.
   */
  @Input() phxCloseIconDisabled: boolean = false;

  /**
   * The CSS class to apply to the drawer.
   */
  @Input() phxOverlayClass: string;

  /**
   * Indicates whether the drawer should have a backdrop.
   */
  @Input() phxBackdrop: boolean = true;

  /**
   * Event emitted when the `phxOpen` property changes.
   */
  @Output() phxOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Event emitted when the backdrop is clicked.
   */
  @Output() phxBackdropClicked: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Event emitted when the close icon is clicked.
   */
  @Output() phxCloseIconClicked: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Event emitted when escape key is pressed while the drawer is open.
   */
  @Output() phxEscapeKeyPressed: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Indicates whether the component is being viewed in a mobile device.
   */
  public isMobileView: boolean = false;

  /**
   * Subscription to the breakpoint observer service.
   */
  public breakpointObserverSubscription: Subscription;

  public readonly CloseIcon: string = Cross;
  public readonly BackIcon: string = ArrowLeft;

  constructor(private _phxBreakPointObserver: PhxBreakpointObserverService, private _cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.breakpointObserverSubscription = this._phxBreakPointObserver.smMobile.subscribe((isMobile) => {
      this.isMobileView = isMobile;
      this._cdr.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    this.breakpointObserverSubscription.unsubscribe();
  }

  /**
   * Handles the backdrop click event.
   */
  public onBackdropClicked(): void {
    this.phxBackdropClicked.emit();
    if (this.phxAutoClose) this.closeDrawer(); 
  }

  /**
   * Closes the drawer and emits the `phxOpenChange` event.
   */
  public closeDrawer(): void {
    this.phxOpen = false;
    this.phxOpenChange.emit(false);
  }

  /**
   * Handles the close icon click event.
   */
  public closeIconClicked(): void {
    this.phxCloseIconClicked.emit();
    if (this.phxAutoClose) this.closeDrawer();   
  }

  /**
   * Handles the keydown event of dialog.
   * @param event - The keyboard event.
   */
  public onKeydown(event: KeyboardEvent): void {
    if (this.phxOpen && event.key === KeyboardKeys.ESC) {
      this.phxEscapeKeyPressed.emit();
      if (this.phxAutoClose) {
        event.preventDefault();
        this.closeDrawer();
      }
    }
  }
}
